// =====================================================
// PAYMENT MODAL
// Stage 3.2: Payment Processing
// Modal for recording participant payments
// =====================================================

import { formatBs } from '@/lib/money';
import { useState, useEffect } from 'react';
import { DollarSign, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PriceInput } from '@/components/ui/price-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PAYMENT_METHOD_LABELS, SELECTABLE_PAYMENT_METHODS, type EventPaymentMethod } from '@/Services/eventPayments.service';
import { useToast } from '@/hooks/use-toast';
import { formatPrice } from '@/models/event';

// ===================
// TYPES
// ===================

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  participantName: string;
  calculatedPrice: number;
  /** Already paid (partial payments) — default amount is the balance */
  paidSoFar?: number;
  onSubmit: (amount: number, notes: string | undefined, method: EventPaymentMethod) => Promise<void>;
  isProcessing?: boolean;
}

// ===================
// COMPONENT
// ===================

export function PaymentModal({
  open,
  onOpenChange,
  participantName,
  calculatedPrice,
  paidSoFar = 0,
  onSubmit,
  isProcessing = false,
}: PaymentModalProps) {
  const { toast } = useToast();
  const balance = Math.max(0, calculatedPrice - paidSoFar);
  const [paymentAmount, setPaymentAmount] = useState<number>(balance);
  const [notes, setNotes] = useState<string>('');
  const [method, setMethod] = useState<EventPaymentMethod>('cash');

  useEffect(() => {
    if (open) {
      setPaymentAmount(balance);
      setNotes('');
      setMethod('cash');
    }
  }, [open, balance]);

  const amountNumber = paymentAmount;
  const remainingAfter = balance - amountNumber;

  const handleSubmit = async () => {
    const amount = paymentAmount;
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Monto inválido', description: 'El monto debe ser mayor a 0', variant: 'destructive' });
      return;
    }
    try {
      await onSubmit(amount, notes.trim() || undefined, method);
    } catch {
      // errors handled by the mutation in PaymentButton
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="size-5" />
            Registrar Pago
          </DialogTitle>
          <DialogDescription>
            Registre el pago realizado por el participante
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Participant Info */}
          <div className="rounded-md bg-muted p-3">
            <p className="text-sm font-medium">{participantName}</p>
            <p className="text-xs text-muted-foreground">
              Precio calculado: {formatBs(calculatedPrice)}
              {paidSoFar > 0 && ` · Pagado: ${formatPrice(paidSoFar)} · Saldo: ${formatPrice(balance)}`}
            </p>
          </div>

          {/* Method */}
          <div className="space-y-2">
            <Label>Método de pago</Label>
            <Select value={method} onValueChange={(v) => setMethod(v as EventPaymentMethod)} disabled={isProcessing}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SELECTABLE_PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Amount */}
          <div className="space-y-2">
            <Label htmlFor="payment-amount">
              Monto del Pago <span className="text-destructive">*</span>
            </Label>
            <PriceInput
              id="payment-amount"
              value={paymentAmount}
              onValueChange={setPaymentAmount}
              disabled={isProcessing}
            />
            <p className={`text-xs ${remainingAfter > 0.009 ? 'text-amber-700' : 'text-muted-foreground'}`}>
              {remainingAfter > 0.009
                ? `Pago parcial: quedará un saldo de ${formatPrice(remainingAfter)}`
                : remainingAfter < -0.009
                  ? `El monto supera el saldo por ${formatPrice(Math.abs(remainingAfter))}`
                  : 'Cubre el total del saldo'}
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="payment-notes">Notas (opcional)</Label>
            <Textarea
              id="payment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Número de referencia, observaciones, etc."
              rows={3}
              disabled={isProcessing}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Procesando...
              </>
            ) : (
              'Registrar Pago'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
